import React, {
    forwardRef,
    useContext,
    useEffect,
    useId,
    useRef,
    useState,
} from 'react';
import classNames from 'classnames';
import { Input as AntInput } from 'antd';
import type { InputRef } from 'antd';
import type { PasswordProps as AntPasswordProps } from 'antd/es/input/Password';
import { FormItemInputContext } from 'antd/es/form/context';
import styles from './input.module.scss';
import { FLOATING_FORM_ITEM_CLASS, isValuePresent } from './utils';

export type AppInputPasswordProps = Omit<AntPasswordProps, 'size'> & {
    size?: 'sm' | 'md' | 'lg';
    label?: React.ReactNode;
    error?: React.ReactNode;
    hint?: React.ReactNode;
    wrapperClassName?: string;
};

export const AppInputPassword = forwardRef<InputRef, AppInputPasswordProps>(
    (props, ref) => {
        const {
            size = 'md',
            label,
            error,
            hint,
            wrapperClassName,
            className,
            status: statusProp,
            id: idProp,
            value,
            defaultValue,
            onFocus,
            onBlur,
            onChange,
            ...restProps
        } = props;

        const formItemContext = useContext(FormItemInputContext);
        const contextStatus = formItemContext?.status;
        const inputStatus =
            statusProp ??
            (contextStatus === 'error' || contextStatus === 'warning'
                ? contextStatus
                : undefined);
        const visualStatus = statusProp ?? contextStatus;

        const controlProps = {
            ...restProps,
        } as AntPasswordProps & Record<string, unknown>;

        const ariaDescribedByProp = controlProps['aria-describedby'] as
            | string
            | undefined;
        const ariaInvalidProp = controlProps['aria-invalid'] as
            | React.AriaAttributes['aria-invalid']
            | undefined;

        delete controlProps['aria-describedby'];
        delete controlProps['aria-invalid'];

        if (!controlProps.autoComplete) {
            controlProps.autoComplete = 'current-password';
        }

        const generatedId = useId();
        const inputId = idProp ?? generatedId;
        const hintId = hint ? `${inputId}-hint` : undefined;
        const errorId = error ? `${inputId}-error` : undefined;

        const describedBy =
            [ariaDescribedByProp, errorId, hintId].filter(Boolean).join(' ') ||
            undefined;

        const ariaInvalid =
            ariaInvalidProp !== undefined
                ? ariaInvalidProp
                : visualStatus === 'error' || Boolean(error)
                ? true
                : undefined;

        const isDisabled = Boolean(controlProps.disabled);
        const hasExplicitError = Boolean(error) && !visualStatus;

        const [isFocused, setIsFocused] = useState(false);
        const [hasValue, setHasValue] = useState(() =>
            isValuePresent(value ?? defaultValue)
        );
        const [inferredLabel, setInferredLabel] = useState<React.ReactNode>();

        useEffect(() => {
            if (value !== undefined) {
                setHasValue(isValuePresent(value));
            }
        }, [value]);

        const handleFocus: AntPasswordProps['onFocus'] = event => {
            setIsFocused(true);
            onFocus?.(event);
        };

        const handleBlur: AntPasswordProps['onBlur'] = event => {
            setIsFocused(false);
            onBlur?.(event);
        };

        const handleChange: AntPasswordProps['onChange'] = event => {
            setHasValue(isValuePresent(event?.target?.value ?? ''));
            onChange?.(event);
        };

        if (value !== undefined) {
            controlProps.value = value;
        }

        if (defaultValue !== undefined) {
            controlProps.defaultValue = defaultValue;
        }

        if (controlProps.bordered === undefined) {
            controlProps.bordered = false;
        }

        controlProps.onFocus = handleFocus;
        controlProps.onBlur = handleBlur;
        controlProps.onChange = handleChange;

        const rootClassName = classNames(
            styles.root,
            styles[size],
            wrapperClassName
        );

        const controlClassName = classNames(styles.control, className);

        const fieldRef = useRef<HTMLDivElement>(null);
        const [prefixOffset, setPrefixOffset] = useState(0);

        const prefixProp = (restProps as AntPasswordProps).prefix;
        const hasPrefix = Boolean(prefixProp);

        const floatingLabel = label ?? inferredLabel;
        const isLabelFloating =
            Boolean(floatingLabel) && (isFocused || hasValue);

        useEffect(() => {
            const fieldNode = fieldRef.current;
            if (!fieldNode) {
                return;
            }

            const formItemNode = fieldNode.closest(
                '.ant-form-item'
            ) as HTMLElement | null;
            if (!formItemNode) {
                return;
            }

            formItemNode.classList.add(FLOATING_FORM_ITEM_CLASS);

            return () => {
                formItemNode.classList.remove(FLOATING_FORM_ITEM_CLASS);
            };
        }, []);

        useEffect(() => {
            if (label) {
                setInferredLabel(undefined);
                return;
            }

            const fieldNode = fieldRef.current;
            if (!fieldNode) {
                return;
            }

            const formItemNode = fieldNode.closest(
                '.ant-form-item'
            ) as HTMLElement | null;
            const labelNode = formItemNode?.querySelector(
                '.ant-form-item-label'
            ) as HTMLElement | null;

            if (!labelNode) {
                setInferredLabel(undefined);
                return;
            }

            const text = labelNode.textContent?.trim();
            setInferredLabel(text || undefined);
        }, [label]);

        useEffect(() => {
            if (!floatingLabel) {
                return;
            }

            const fieldNode = fieldRef.current;
            if (!fieldNode) {
                return;
            }

            const prefixNode = fieldNode.querySelector(
                '.ant-input-prefix'
            ) as HTMLElement | null;

            if (prefixNode) {
                setPrefixOffset(prefixNode.offsetWidth);
            } else {
                setPrefixOffset(0);
            }
        }, [floatingLabel, hasPrefix, prefixProp]);

        const labelStyle = prefixOffset
            ? ({ left: `${prefixOffset}px` } as React.CSSProperties)
            : undefined;

        const fieldClassName = classNames(styles.field, {
            [styles['field--is-floating']]: isLabelFloating,
            [styles['field--is-focused']]: isFocused,
        });

        return (
            <div
                className={rootClassName}
                data-status={visualStatus ?? undefined}
                data-disabled={isDisabled}
                data-has-error={hasExplicitError ? 'true' : undefined}
            >
                <div
                    ref={fieldRef}
                    className={fieldClassName}
                    data-floating={isLabelFloating ? 'true' : undefined}
                >
                    {floatingLabel ? (
                        <label
                            className={styles.label}
                            htmlFor={inputId}
                            style={labelStyle}
                        >
                            {floatingLabel}
                        </label>
                    ) : null}
                    <AntInput.Password
                        {...(controlProps as AntPasswordProps)}
                        ref={ref}
                        id={inputId}
                        status={inputStatus}
                        className={controlClassName}
                        aria-describedby={describedBy}
                        aria-invalid={ariaInvalid}
                    />
                </div>
                {error || hint ? (
                    <div className={styles.meta}>
                        {error ? (
                            <span className={styles.error} id={errorId}>
                                {error}
                            </span>
                        ) : null}
                        {hint ? (
                            <span className={styles.hint} id={hintId}>
                                {hint}
                            </span>
                        ) : null}
                    </div>
                ) : null}
            </div>
        );
    }
);

AppInputPassword.displayName = 'AppInputPassword';
